import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { Bars3Icon, CursorArrowRippleIcon, EnvelopeIcon, PowerIcon, Squares2X2Icon, UserIcon } from '@heroicons/react/24/solid';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { api } from '../api';
import { localSignOut } from '../features/users/loginSlice';
import { clearUserInfo, selectUserInfo } from '../features/users/userInfoSlice';
import { navigationOptions } from './SideBar';
import Logo from '../images/Logo_Azatys.png';
import { StandardDialog } from './modals';
import { Popover, Transition } from '@headlessui/react';
import { classNames } from '../utils';
import { tour } from '../configTour';
import { toggleLaunch } from '../features/tour/tourSlice';
import { CONTACT_LINK } from '../constants';

const titleMap = {
  account: 'MON COMPTE',
  websites: 'MES SITES INTERNET'
};

navigationOptions.forEach(nav => {
  titleMap[nav.href.toLowerCase().replace('/', '')] = `${nav.article ?? "MON"} ${nav.name.toUpperCase()}`
})

const TopNavBar = ({ }) => {
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const userContext = useSelector(selectUserInfo);

  const _logout = async () => {
    dispatch(localSignOut());
    dispatch(clearUserInfo());
    await api.auth().signOut();
    window.location.assign('/login');
  };

  useEffect(() => {
    tour.on('cancel', () => {
      dispatch(toggleLaunch())
    })
    tour.on('complete', () => {
      dispatch(toggleLaunch())
    })
  }, [tour])

  const toggleShow = useCallback(() => {
    setOpen((s) => !s);
  }, []);

  const opts = [
    {
      icon: <UserIcon className="w-5 h-5 mr-2" />,
      label: 'Mon compte',
      action: () => {
        history.push('/account');
      }
    },
    {
      icon: <Squares2X2Icon className="w-5 h-5 mr-2" />,
      label: 'Mes sites',
      action: () => {
        history.push('/websites');
      }
    },
    {
      icon: <EnvelopeIcon className="w-4 h-4+
       mr-2" />,
      label: 'Contactez-nous',
      action: () => {
        history.push('/contact', { link: CONTACT_LINK })
      }
    },
    {
      icon: <CursorArrowRippleIcon className="w-4 h-4+
       mr-2" />,
      label: 'Tour Guide App',
      action: () => {
        tour.start()
        dispatch(toggleLaunch())
      }
    },
    {
      icon: <PowerIcon className="w-5 h-5 mr-2" />,
      label: 'Déconnexion',
      action: () => {
        toggleShow();
      }
    }
  ];

  const _toggleShowSidebar = () => {
    const sidebarEl = document.querySelector('#sidebar');
    const classShow = 'sidebar-show';
    const classHide = 'sidebar-hide';
    sidebarEl.classList.toggle(classHide);
    sidebarEl.classList.toggle(classShow);
  };

  return (
    // <div classname=" text-center w-full py-2">
    <div className="sticky top-0 w-full bg-blue-900 text-center uppercase text-white font-bold mx-auto py-3 px-3 z-40 mb-3">
      <div className="flex justify-between items-center">
        <div className="flex justify-center items-center space-x-2 md:hidden">
          <button type="button" onClick={_toggleShowSidebar}>
            <Bars3Icon className="text-white h-6" />
          </button>
          <img className="h-10 w-auto" src={Logo} alt="Your Company" />
        </div>
        <h1 className="uppercase text-white tracking-wider flex-grow">
          {titleMap[history.location.pathname.split('/')[1]] ?? 'ACCUEIL'}
        </h1>
        <Popover className="relative pop-parent">
          {({ open }) => (
            <>
              <Popover.Button
                className={classNames(
                  'rounded-full p-2 hover:bg-white/10 transition-colors first-step',
                  open ? 'text-teal-500' : 'text-white'
                )}
              >
                <UserIcon className="w-7 h-7" />
              </Popover.Button>
              <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel className="absolute top-9 left-1/2 mt-3 w-44 -translate-x-[130px] transform px-4 sm:px-0 z-50">
                  <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="bg-blue-900/95 backdrop-filter backdrop-blur">
                      {opts.map((op, i) => {
                        if (op.label === 'Mes sites' || op.label === 'Tour Guide App') {
                          if (userContext?.user?.admin) return (
                            <button
                              key={i}
                              type="button"
                              onClick={op.action}
                              className={`flex items-center text-sm text-white px-3 py-2 hover:bg-blue-900 hover:text-teal-500 transition-colors w-full redirect-step2 ${op.label === 'Tour Guide App' ? 'user-guide' : ''}`}
                            >
                              {op.icon} <span>{op.label}</span>
                            </button>
                          )
                          else return null
                        }

                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={op.action}
                            className={`flex items-center text-sm text-white px-3 py-2 hover:bg-blue-900 hover:text-teal-500 transition-colors w-full ${i === 0 ? 'redirect-step5' : ''}`}
                          >
                            {op.icon} <span>{op.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
      <StandardDialog open={open} onClose={toggleShow}>
        <div className="rounded-xl bg-white px-5 py-2 flex flex-col justify-center items-center space-y-10">
          <div className="flex flex-col items-center">
            <PowerIcon className="text-teal-800 h-16" />
            <h2 className="text-xl font-semibold text-gray-600">Voulez-vous vraiment vous déconnecter ?</h2>
          </div>
          <div className="flex items-center justify-around w-full space-x-2">
            <button
              type="button"
              onClick={_logout}
              className="rounded-full bg-gray-300 py-3 w-full text-gray-600 hover:bg-gray-200 transition-colors uppercase tracking-wider font-medium text-sm flex-1"
            >
              Oui
            </button>
            <button
              type="button"
              onClick={toggleShow}
              className="rounded-full bg-teal-700 hover:bg-teal-800 py-3 w-full text-white transition-colors uppercase tracking-wider font-medium text-sm flex-1"
            >
              Non
            </button>
          </div>
        </div>
      </StandardDialog>
    </div>
  );
};

export default TopNavBar;
